import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Modal,
  ModalHeader,
  ModalHeaderTitle,
  ModalBody,
  ModalFooter,
  Button,
  ButtonEmpty,
  Text,
} from '@antoree/ant-ui';
import { videoStorage, StoredVideo } from '../../utils/videoStorage';
import styles from './PendingVideosModal.module.scss';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const PendingVideosModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const [videos, setVideos] = useState<StoredVideo[]>([]);
  const [loading, setLoading] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    if (isOpen) {
      loadPendingVideos();
    }
  }, [isOpen]);

  const loadPendingVideos = async () => {
    setLoading(true);
    try {
      // Fetch ALL videos (pending, failed, and success) so teachers can view and delete them anytime
      const allVideos = await videoStorage.getAllVideos();
      setVideos(allVideos.sort((a, b) => b.timestamp - a.timestamp)); // Newest first
    } catch (err) {
      console.error('Failed to load pending videos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (video: StoredVideo) => {
    const url = URL.createObjectURL(video.blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${video.sessionTitle.replace(/[^a-z0-9]/gi, '_')}_${new Date(
      video.timestamp,
    ).toISOString()}.webm`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleDelete = async (videoId: string) => {
    if (
      confirm(
        intl.formatMessage({
          id: 'pending_videos.delete_confirm',
          defaultMessage:
            'Are you sure you want to delete this video?\n\nPlease make sure the video has been successfully uploaded to the session first. This action cannot be undone.',
        }),
      )
    ) {
      try {
        await videoStorage.deleteVideo(videoId);
        await loadPendingVideos();
      } catch (err) {
        console.error('Failed to delete video:', err);
      }
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isOpen) return null;

  return (
    <Modal onClose={onClose} className={styles.modal}>
      <ModalHeader>
        <ModalHeaderTitle>
          <FormattedMessage
            id="pending_videos.title"
            defaultMessage="Saved Videos"
          />
        </ModalHeaderTitle>
      </ModalHeader>

      <ModalBody>
        {loading ? (
          <Text>
            <FormattedMessage id="common.loading" defaultMessage="Loading..." />
          </Text>
        ) : videos.length === 0 ? (
          <Text>
            <FormattedMessage
              id="pending_videos.no_videos"
              defaultMessage="No saved videos found in local storage."
            />
          </Text>
        ) : (
          <div className={styles.videoList}>
            {videos.map(video => (
              <div key={video.id} className={styles.videoItem}>
                <div className={styles.videoInfo}>
                  <Text className={styles.title}>{video.sessionTitle}</Text>
                  <Text className={styles.meta}>
                    {formatDate(video.timestamp)} •{' '}
                    {formatFileSize(video.blob.size)}
                  </Text>
                  <Text className={styles.status}>
                    <FormattedMessage
                      id="pending_videos.status"
                      defaultMessage="Status: {status}"
                      values={{ status: video.uploadStatus }}
                    />
                    {video.uploadAttempts > 0 && (
                      <FormattedMessage
                        id="pending_videos.attempts"
                        defaultMessage=" ({attempts} attempts)"
                        values={{ attempts: video.uploadAttempts }}
                      />
                    )}
                  </Text>
                  {video.lastError && (
                    <Text className={styles.error}>
                      <FormattedMessage
                        id="pending_videos.error"
                        defaultMessage="Error: {error}"
                        values={{ error: video.lastError }}
                      />
                    </Text>
                  )}
                </div>

                <div className={styles.actions}>
                  <Button onClick={() => handleDownload(video)} size="s">
                    <FormattedMessage
                      id="common.download"
                      defaultMessage="Download"
                    />
                  </Button>
                  <ButtonEmpty
                    onClick={() => handleDelete(video.id)}
                    size="s"
                    color="danger"
                  >
                    <FormattedMessage
                      id="common.delete"
                      defaultMessage="Delete"
                    />
                  </ButtonEmpty>
                </div>
              </div>
            ))}
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        <ButtonEmpty onClick={onClose}>
          <FormattedMessage id="common.close" defaultMessage="Close" />
        </ButtonEmpty>
      </ModalFooter>
    </Modal>
  );
};

export default PendingVideosModal;
